<template>
  <b-modal ref="modalRef" centered title="Withdraw award" @hidden="reset">
    <template v-if="award">
      <p>
        Withdraw the <strong>{{ awardDescription }}</strong> award for <strong>{{ award.full_name }}</strong
        >, due on <strong>{{ formattedDate }}</strong
        >?
      </p>
      <p class="text-muted">
        They won't receive it unless you reinstate it, which you can do up to the day before it's due.
      </p>
    </template>

    <b-form-group label="Reason (optional)" label-for="withdraw-upcoming-award-reason" class="mb-0">
      <b-form-input
        id="withdraw-upcoming-award-reason"
        v-model="reason"
        placeholder="e.g. Leaving before their birthday"
        :maxlength="500"
      />
    </b-form-group>

    <template #modal-footer>
      <b-button variant="outline-secondary" :disabled="isSubmitting" @click="hide">Cancel</b-button>
      <b-button variant="danger" :disabled="isSubmitting" @click="onConfirm">
        <b-spinner v-if="isSubmitting" small class="mr-1" />
        Withdraw award
      </b-button>
    </template>
  </b-modal>
</template>

<script>
// A plain <script> with setup() rather than <script setup>, so Jest can mount it.
import { computed, ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { format, parseISO } from 'date-fns';
import { toCurrency } from '@/utils';
import useToast from '@/composables/useToast';
import { withdrawUpcomingMilestoneAward } from '@/queries/recognition';
import { upcomingAwardError } from '@/pages/recognition/constants';
import State from '@/state/State';

export default {
  name: 'WithdrawUpcomingAwardModal',
  props: {
    award: {
      type: Object,
      default: null,
    },
  },
  // Emitted whenever the Upcoming list is out of date: after a withdrawal, or when the API
  // says the award had already changed.
  emits: ['changed'],
  setup(props, { emit }) {
    const { doToast, TOAST_VARIANT } = useToast();

    const modalRef = ref(null);
    const reason = ref('');

    const withdrawMutation = useMutation(withdrawUpcomingMilestoneAward());
    const isSubmitting = computed(() => withdrawMutation.isPending.value);

    const formattedDate = computed(() =>
      props.award ? format(parseISO(props.award.scheduled_date), 'dd MMM yyyy') : '',
    );

    const awardDescription = computed(() => {
      const { amount_minor: amountMinor, program_name: programName } = props.award || {};
      if (!amountMinor) {
        return programName;
      }
      return `${toCurrency(amountMinor, State.state?.company?.default_currency)} ${programName}`;
    });

    const show = () => modalRef.value?.show();
    const hide = () => modalRef.value?.hide();

    const reset = () => {
      reason.value = '';
    };

    const onConfirm = async () => {
      try {
        await withdrawMutation.mutateAsync({ award: props.award, reason: reason.value.trim() || null });
        doToast(
          'Success',
          `${props.award.program_name} award for ${props.award.full_name} withdrawn`,
          TOAST_VARIANT.SUCCESS,
        );
        emit('changed');
      } catch (e) {
        const { message, isStale } = upcomingAwardError(e, 'Failed to withdraw award. Please try again.');
        doToast('Error', message, TOAST_VARIANT.DANGER);
        if (isStale) {
          emit('changed');
        }
      }
      hide();
    };

    return { modalRef, reason, isSubmitting, formattedDate, awardDescription, show, hide, reset, onConfirm };
  },
};
</script>
